import { OrganizationMetrics } from '@prisma/client'
import pluralize from 'pluralize'
import { AgentKey } from '~/agents/common/common/types'
import {
  OrganizationMetricsEmployeeConfig,
  OrganizationMetricsTaskConfig,
} from '~/features/organizationMetrics/common/objects'
import {
  OrganizationMetricsTaskComputed,
  OrganizationMetricsTaskConfigType,
  OrganizationMetricsTasks,
  OrganizationMetricsTaskType,
} from '~/features/organizationMetrics/common/types'

type Props = {
  organizationMetrics: OrganizationMetrics
}

type TaskMetrics = OrganizationMetricsTaskComputed & {
  config: OrganizationMetricsTaskConfigType
}

export const useLogic = ({ organizationMetrics }: Props) => {
  const getLastMonthMetricsForAgent = (agentKey: AgentKey) => {
    const metrics: Partial<Record<OrganizationMetricsTaskType, TaskMetrics>> =
      {}

    const tasks = organizationMetrics?.tasks as OrganizationMetricsTasks

    for (const [taskType, taskCount] of Object.entries(tasks)) {
      const taskConfig =
        OrganizationMetricsTaskConfig[taskType as OrganizationMetricsTaskType]

      const employeeConfig =
        OrganizationMetricsEmployeeConfig[taskConfig.employee]

      if (employeeConfig.appKey !== agentKey) {
        continue
      }

      metrics[taskType as OrganizationMetricsTaskType] = {
        config: taskConfig,
        count: taskCount,
        timeSaved: taskCount * taskConfig.timeEstimate,
        moneySaved:
          taskCount * taskConfig.timeEstimate * employeeConfig.hourlyRate,
      }
    }

    const metricsArray = Object.entries(metrics).map(([type, data]) => ({
      type: type as OrganizationMetricsTaskType,
      ...data,
    }))

    return metricsArray
  }

  const getCountWithUnit = (count: number): string => {
    const hours = Number(Math.floor(count * 2) / 2)
    const minutes = Number((count * 60).toFixed(0))
    const seconds = Number((minutes * 60).toFixed(0))

    if (seconds < 60) {
      return pluralize('second', seconds, true)
    }

    if (minutes < 90) {
      return pluralize('minute', minutes, true)
    }

    return pluralize('hour', hours, true)
  }

  const getCountWithUnitMinified = (count: number): string => {
    const hours = Number(Math.floor(count * 2) / 2)
    const minutes = Number((count * 60).toFixed(0))
    const seconds = Number((count * 60 * 60).toFixed(0))

    if (seconds < 60) {
      return `${seconds}s`
    }

    if (minutes < 90) {
      return `${minutes}min`
    }

    return `${hours}h`
  }

  return {
    getLastMonthMetricsForAgent,
    getCountWithUnit,
    getCountWithUnitMinified,
  }
}
